import React, { useState, useEffect } from 'react';
import { MessageCircle, ChevronUp, X } from 'lucide-react';

interface FloatingTutorButtonProps {
  onClick: () => void;
}

const FloatingTutorButton: React.FC<FloatingTutorButtonProps> = ({ onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Detect mobile and responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Track scroll to hide button when scrolling up and show when scrolling down
  useEffect(() => {
    let lastScrollY = 0;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show button when at top or scrolling down
      // Hide button when scrolling down significantly
      if (currentScrollY < lastScrollY) {
        setIsVisible(true); // Scrolling up
      } else if (currentScrollY - lastScrollY > 50) {
        setIsVisible(false); // Scrolling down more than 50px
      }
      
      lastScrollY = currentScrollY;
      setScrollPosition(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Position adjustment based on scroll
  const getBottomPosition = () => {
    if (isMobile) {
      return isVisible ? 'bottom-4' : 'bottom-0 translate-y-24';
    }
    return isVisible ? 'bottom-8' : 'bottom-0 translate-y-32';
  };

  const getRightPosition = () => {
    // Desktop: fixed right position
    // Mobile: slightly more centered to avoid thumb area
    return isMobile ? 'right-4' : 'right-8';
  };

  const getButtonSize = () => {
    return isMobile ? 'w-14 h-14' : 'w-16 h-16';
  };

  const getIconSize = () => {
    return isMobile ? 24 : 28;
  };

  return (
    <div className={`fixed ${getRightPosition()} ${getBottomPosition()} z-50 group transition-all duration-500 ease-out`}>
      {/* Tooltip Label - responsive positioning */}
      <div
        className={`absolute right-0 px-3 py-2 md:px-4 md:py-2 bg-gradient-to-r from-red-600 to-rose-600 text-white rounded-lg font-semibold text-xs md:text-sm whitespace-nowrap shadow-xl transition-all duration-300 pointer-events-none ${
          isHovered ? 'opacity-100 translate-y-0 visible' : 'opacity-0 translate-y-2 invisible'
        } ${
          isMobile ? 'bottom-16 right-0' : 'bottom-20 right-0'
        }`}
      >
        <div className="flex items-center gap-2">
          <span>Ask AI Tutor</span>
          <ChevronUp size={14} className="md:w-4 md:h-4" />
        </div>
        {/* Tooltip arrow */}
        <div className="absolute -bottom-2 right-4 w-2 h-2 bg-gradient-to-r from-red-600 to-rose-600 rotate-45" />
      </div>

      {/* Main Button - Responsive sizing */}
      <button
        onClick={onClick}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => !isMobile && setIsHovered(false)}
        onTouchStart={() => isMobile && setIsHovered(true)}
        onTouchEnd={() => isMobile && setIsHovered(false)}
        aria-label="Open AI Tutor Chat"
        className={`relative ${getButtonSize()} rounded-full bg-gradient-to-br from-red-500 to-rose-600 text-white shadow-lg md:shadow-2xl transition-all duration-300 ease-out hover:shadow-red-500/50 flex items-center justify-center group/btn overflow-hidden active:scale-95 ${
          isHovered ? 'scale-110 shadow-red-500/75 md:scale-110' : 'scale-100 hover:scale-105 md:hover:scale-105'
        }`}
      >
        {/* Background animated gradient on hover */}
        <div
          className={`absolute inset-0 bg-gradient-to-br from-rose-400 to-red-600 opacity-0 transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Animated circle background */}
        <div
          className={`absolute inset-0 rounded-full border-2 border-white/30 transition-all duration-500 ${
            isHovered && !isMobile ? 'animate-pulse' : ''
          }`}
        />

        {/* Icon with animation */}
        <div className="relative z-10 transition-transform duration-300">
          <MessageCircle
            size={getIconSize()}
            className={`transition-transform duration-300 ${
              isHovered ? 'rotate-12 scale-110' : 'rotate-0 scale-100'
            }`}
          />
        </div>

        {/* Pulse ring effect on hover - desktop only */}
        {isHovered && !isMobile && (
          <>
            <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-[ping_1.5s_cubic-bezier(0,0,0.2,1)_infinite]" />
            <div className="absolute inset-2 rounded-full border border-white/10 animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]" />
          </>
        )}
      </button>

      {/* Badge indicator - shows new messages or active status */}
      <div className="absolute -top-2 -right-2 w-5 h-5 bg-yellow-400 rounded-full border-2 border-white shadow-md flex items-center justify-center">
        <span className="text-xs font-bold text-gray-900">!</span>
      </div>

      {/* Mobile hint - show on first load for mobile users */}
      {isMobile && isVisible && !isHovered && (
        <div className="absolute -left-32 md:hidden bottom-4 px-2 py-1 bg-gray-900 text-white rounded text-xs whitespace-nowrap shadow-lg opacity-70 animate-pulse">
          💬 Tap for help
        </div>
      )}
    </div>
  );
};

export default FloatingTutorButton;
